import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { lookupPostcode, type AddressSuggestion } from '@/lib/postcode';

export interface GuestAddress {
  name: string; line1: string; line2?: string; city: string; postcode: string; country: string; phone?: string;
}
export interface GuestCheckoutPayload {
  guest: { email: string; name: string };
  shippingAddress: GuestAddress;
}

/**
 * Collects a signed-out buyer's details so they can check out without creating
 * an account. The backend provisions a passwordless guest buyer from this and
 * emails an order-tracking link; the buyer can set a password later to claim
 * the account. These details are used to deliver the parcel and keep the buyer
 * updated on their order.
 */
export function GuestCheckoutModal({
  open, onClose, submitting, onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  onConfirm: (p: GuestCheckoutPayload) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('United Kingdom');
  const [phone, setPhone] = useState('');
  const [looking, setLooking] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  // Lock the page behind the modal from scrolling while it is open, so only the
  // modal's own body scrolls and the Continue-to-payment button stays reachable.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const runLookup = async () => {
    if (postcode.trim().length < 5) { toast.error('Enter a full postcode first.'); return; }
    setLooking(true);
    const r = await lookupPostcode(postcode);
    setLooking(false);
    if (!r) { toast('Postcode not found. Add your address manually.', { icon: '✍️' }); return; }
    if (r.city) setCity(r.city);
    if (r.country) setCountry(r.country);
    if (r.postcode) setPostcode(r.postcode);
    if (r.suggestions.length > 0) {
      setSuggestions(r.suggestions);
      toast.success(`${r.suggestions.length} addresses found. Pick yours below.`);
    } else {
      setSuggestions([]);
      toast.success(`${r.city ?? r.region ?? 'Area'} found. Now add your street (line 1).`);
    }
  };

  const submit = () => {
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
    if (!name.trim()) return toast.error('Please add your full name.');
    if (!emailOk) return toast.error('Please add a valid email address.');
    if (!line1.trim()) return toast.error('Please add your street address (line 1).');
    if (!city.trim()) return toast.error('Please add your town or city.');
    if (postcode.trim().length < 5) return toast.error('Please add a full postcode.');
    onConfirm({
      guest: { email: email.trim().toLowerCase(), name: name.trim() },
      shippingAddress: {
        name: name.trim(), line1: line1.trim(), line2: line2.trim() || undefined,
        city: city.trim(), postcode: postcode.trim().toUpperCase(), country: country.trim() || 'United Kingdom',
        phone: phone.trim() || undefined,
      },
    });
  };

  const field = 'w-full rounded-xl border border-warmline bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-mute focus:border-gold focus:ring-2 focus:ring-gold/25 focus:outline-none transition';
  const label = 'block text-[11px] font-semibold uppercase tracking-wider text-ink-700 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        role="dialog" aria-modal="true"
        className="flex w-full max-w-[480px] max-h-[90dvh] flex-col overflow-hidden rounded-2xl bg-cream shadow-2xl ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Vibrant header (fixed) */}
        <div
          className="section-light relative shrink-0 overflow-hidden px-6 pt-6 pb-5 text-ink"
          style={{ background: 'linear-gradient(135deg,#FBE7C2 0%,#F0CE96 45%,#E0A94F 100%)' }}
        >
          <div className="pointer-events-none absolute -right-8 -top-12 w-36 h-36 rounded-full bg-white/30 blur-2xl" />
          <div className="pointer-events-none absolute -left-10 -bottom-14 w-32 h-32 rounded-full bg-white/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
                ⚡ Guest checkout
              </span>
              <h2 className="display text-[26px] leading-tight mt-2.5">Just a few details</h2>
              <p className="text-ink/70 text-[12.5px] mt-1.5 max-w-[34ch]">
                We'll email you a tracking link and use these to deliver your order. Payment secured by Stripe.
              </p>
            </div>
            <button
              type="button" onClick={onClose} aria-label="Close"
              className="shrink-0 w-8 h-8 grid place-items-center rounded-full bg-white/40 hover:bg-white/80 text-ink transition"
            >✕</button>
          </div>
        </div>

        {/* Body - scrolls on its own so every field stays reachable */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 space-y-5">
          <div>
            <label className={label}>Your details</label>
            <div className="space-y-2.5">
              <input className={field} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              <input className={field} placeholder="Email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </div>
          </div>

          <div>
            <label className={label}>Delivery address</label>
            <div className="space-y-2.5">
              <input className={field} placeholder="Address line 1" value={line1} onChange={(e) => setLine1(e.target.value)} autoComplete="address-line1" />
              <input className={field} placeholder="Address line 2 (optional)" value={line2} onChange={(e) => setLine2(e.target.value)} autoComplete="address-line2" />
              <div className="flex gap-2">
                <input className={field} placeholder="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} autoComplete="postal-code" />
                <button
                  type="button" onClick={runLookup} disabled={looking}
                  className="shrink-0 whitespace-nowrap rounded-xl border border-gold bg-gold/10 px-4 text-sm font-semibold text-gold-dark hover:bg-gold/20 transition disabled:opacity-60"
                >{looking ? 'Finding...' : 'Find'}</button>
              </div>
              {suggestions.length > 0 && (
                <select className={field} defaultValue=""
                  onChange={(e) => { const sug = suggestions[Number(e.target.value)]; if (sug) { setLine1(sug.line1); if (sug.line2) setLine2(sug.line2); if (sug.city) setCity(sug.city); setSuggestions([]); } }}>
                  <option value="" disabled>Select your address ({suggestions.length} found)…</option>
                  {suggestions.map((sug, i) => <option key={i} value={i}>{sug.label}</option>)}
                </select>
              )}
              <input className={field} placeholder="Town / city" value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" />
              <input className={field} placeholder="Country" value={country} onChange={(e) => setCountry(e.target.value)} autoComplete="country-name" />
              <input className={field} placeholder="Mobile number (texts your InPost locker code)" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {['🔒 Stripe secured', '📦 Tracked delivery', '🛡️ Buyer protection'].map((b) => (
              <span key={b} className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[10.5px] font-medium text-ink-700 ring-1 ring-warmline">{b}</span>
            ))}
          </div>
        </div>

        {/* Footer - pinned, so the continue/pay button is always visible and clickable */}
        <div className="shrink-0 border-t border-warmline bg-cream px-6 py-4 space-y-2.5">
          <button
            type="button" onClick={submit} disabled={submitting}
            className="btn btn-lg btn-primary w-full !rounded-xl shadow-lg shadow-gold/25"
          >{submitting ? 'Starting checkout...' : 'Continue to payment →'}</button>
          <p className="text-[11.5px] text-mute text-center">
            Already have an account?{' '}
            <button type="button" className="font-semibold text-gold-dark hover:underline" onClick={onClose}>Sign In</button>
          </p>
        </div>
      </div>
    </div>
  );
}
